import {
  ContactInput,
  PhoneType,
  EmailType,
  PostalAddressType,
} from "@capacitor-community/contacts";
import { BusinessCard } from "../models/business-card.model";

/**
 * Converte una BusinessCard in ContactInput compatibile con il plugin Contacts di Capacitor.
 * Se profile_pic è un URL, l'immagine viene scaricata e convertita in base64.
 */
export async function mapBusinessCardToContactInput(
  card: BusinessCard,
  shareUrl: string,
): Promise<ContactInput> {
  let base64Image: string | null = null;

  // 🔹 Se c'è un URL immagine, scaricala e converti in base64
  if (card.profile_pic) {
    try {
      const response = await fetch(card.profile_pic);
      const blob = await response.blob();
      const buffer = await blob.arrayBuffer();
      base64Image = `${arrayBufferToBase64(buffer)}`;
    } catch (err) {
      console.warn("Impossibile scaricare o convertire l’immagine:", err);
    }
  }

  const contact: ContactInput = {
    name: {
      given: card.first_name ?? card.firstName,
      family: card.last_name ?? card.lastName,
    },
    organization: {
      company: card.company_name ?? card.company ?? null,
      jobTitle: card.job_title ?? card.jobTitle ?? null,
    },
    phones: [
      ...(card.phone
        ? [
            {
              type: PhoneType.Main,
              number: card.phone,
              isPrimary: true,
              label: "main",
            },
          ]
        : []),
      ...(card.secondary_phones?.map((p) => ({
        type: PhoneType.Other,
        number: p.number ?? null,
        label: p.label ?? null,
        isPrimary: false,
      })) ?? []),
    ],
    emails: [
      ...(card.email
        ? [
            {
              type: EmailType.Work,
              address: card.email,
              isPrimary: true,
              label: "main",
            },
          ]
        : []),
      ...(card.secondary_emails?.map((e) => ({
        type: EmailType.Other,
        address: e.email ?? null,
        label: e.label ?? null,
        isPrimary: false,
      })) ?? []),
    ],
    urls: card.website ? [shareUrl, card.website] : [shareUrl],
    postalAddresses: card.address
      ? [
          {
            type: PostalAddressType.Work,
            street:
              [card.address.street, card.address.street_number]
                .filter(Boolean)
                .join(" ") || null,
            city: card.address.city ?? null,
            region: card.address.state ?? null,
            label: "work",
            isPrimary: true,
          },
        ]
      : [],
  };

  if (base64Image) {
    contact.image = { base64String: base64Image };
  }

  return contact;
}

/**
 * Utility per convertire un ArrayBuffer in stringa base64.
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000; // per gestire immagini grandi
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + chunkSize) as unknown as number[],
    );
  }
  return btoa(binary);
}
